import { Link } from "react-router-dom";
import { Clock, Inbox } from "lucide-react";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { EmptyState } from "@/components/ui/feedback";
import { KVList } from "@/components/data-display/kv-list";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UnavailableValue } from "@/components/data-display/unavailable-value";
import { WIP_ACTION_HINT, WipHint } from "@/components/ui/wip-hint";
import type { ReactNode } from "react";

/**
 * Evidence packet detail (`/evidence/:id`) — WIP layout port of the designer's
 * `features/oversight/evidence.tsx` (`EvidenceDetail`).
 *
 * No backend publishes sealed evidence packets in this environment (ALL-5709 /
 * ALL-5778: the authored fixture packets were removed), so this is the designer's
 * structure — breadcrumb, header, a 320px packet panel beside a timeline panel and
 * the reference tabs — with every value rendered as the shared unavailable
 * placeholder and every panel body a designed empty state.
 *
 * The route param is deliberately **not** read. The old fixture ledger resolved a
 * packet by id, and a page with no packet source must not print the URL segment
 * back as if it were a record reference: an id in the URL is not evidence that a
 * packet with that id exists. So every id renders the same honest page.
 */

/** The designer's packet fields, in the designer's order. Values are unavailable, not invented. */
const PACKET_FIELDS = ["Packet ID", "Type", "Subject", "Actor", "Created", "Status"];

/** The designer's reference tabs. */
const REFERENCE_TABS = [
  {
    value: "policy",
    label: "Policy & Risk",
    title: "No policy or risk capture",
    description: "No evidence service records a policy evaluation or risk receipt in this environment yet.",
  },
  {
    value: "screening",
    label: "Screening",
    title: "No screening result",
    description: "No screening provider is connected in this environment yet, so no result, address or verdict exists.",
  },
  {
    value: "provider",
    label: "Provider Metadata",
    title: "No provider metadata",
    description: "No custody provider metadata is published for this packet in this environment yet.",
  },
  {
    value: "mesh",
    label: "Mesh Metadata",
    title: "No mesh metadata",
    description: "No signing mesh or HSM metadata is published for this packet in this environment yet.",
  },
];

/** Id of the hidden node carrying WIP_ACTION_HINT for the disabled controls on the page. */
const WIP_ACTIONS_ID = "evidence-detail-wip-actions";

/** The designer's panel chrome: bordered section with an optional title bar. */
function Panel({
  title,
  bodyClassName = "px-5 py-4",
  children,
}: {
  title?: string;
  bodyClassName?: string;
  children: ReactNode;
}) {
  return (
    <section className="mb-4 overflow-hidden rounded-lg border border-line bg-surface">
      {title ? (
        <header className="border-b border-line px-4 py-2">
          <h3 className="text-sm font-bold text-ink">{title}</h3>
        </header>
      ) : null}
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

export default function EvidenceDetailPage() {
  return (
    <div>
      <Breadcrumb items={[{ label: "Evidence", href: "/evidence" }, { label: "Evidence packet" }]} />

      <div className="mb-5">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-[24px] font-bold leading-tight tracking-tight text-ink">Evidence packet</h1>
          <WipHint />
        </div>
        <p className="mt-1 text-sm text-muted">
          Detail for a single packet: its sealed record, its timeline and its reference metadata.
        </p>
      </div>
      {/* One plain sentence: this area has no backend here, so nothing below is a real sealed record.
          (An `:id` in the URL is not read, and is never printed back as a record reference.) */}
      <span id={WIP_ACTIONS_ID} className="sr-only">
        {WIP_ACTION_HINT}
      </span>
      <p className="mb-5 text-sm text-muted">
        Evidence has no backend in this environment yet, so no packet, timeline event or metadata on this page
        reflects a real sealed record.
      </p>

      <div className="grid gap-5 lg:grid-cols-[320px_minmax(0,1fr)]">
        <div>
          <Panel title="Packet">
            <KVList rows={PACKET_FIELDS.map((field) => ({ k: field, v: <UnavailableValue /> }))} />
          </Panel>
        </div>

        <div>
          <Panel title="Timeline">
            <EmptyState
              title="No timeline events"
              description="No evidence service records a packet timeline in this environment yet, so no event, actor or timestamp can be shown."
              icon={<Clock aria-hidden className="size-5 text-muted" />}
              className="py-8"
            />
          </Panel>

          <Panel bodyClassName="px-5 pb-5 pt-1">
            <Tabs defaultValue={REFERENCE_TABS[0].value}>
              <TabsList className="mb-4">
                {REFERENCE_TABS.map((tab) => (
                  <TabsTrigger key={tab.value} value={tab.value}>
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              {REFERENCE_TABS.map((tab) => (
                <TabsContent key={tab.value} value={tab.value}>
                  <EmptyState
                    title={tab.title}
                    description={tab.description}
                    icon={<Inbox aria-hidden className="size-5 text-muted" />}
                    className="py-8"
                  />
                </TabsContent>
              ))}
            </Tabs>
          </Panel>
        </div>
      </div>

      <section aria-labelledby="evidence-detail-available-heading" className="mx-auto mt-6 max-w-[520px]">
        <h2 id="evidence-detail-available-heading" className="mb-2 text-sm font-bold text-ink">
          What&apos;s available now
        </h2>
        <ul className="divide-y divide-line rounded-lg border border-line bg-surface">
          <li>
            <Link to="/admin/audit-log" className="block px-4 py-3 hover:bg-bg">
              <span className="text-sm font-semibold text-primary">Audit Trail</span>
              <span className="block text-xs text-muted">
                The live trail of administrative actions, which is available today.
              </span>
            </Link>
          </li>
          <li>
            <Link to="/admin/trace-lookup" className="block px-4 py-3 hover:bg-bg">
              <span className="text-sm font-semibold text-primary">Trace lookup</span>
              <span className="block text-xs text-muted">
                Look up a request by correlation id, which is available today.
              </span>
            </Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
